// Jedyne źródło map statusów/typów dokumentu dla modułu faktur.
//
// Przed migracją `pages/Invoices.js` i `pages/InvoicePreview.js` miały WŁASNE kopie
// `PAYMENT_STATUS_LABELS`/`PAYMENT_STATUS_COLORS` (i `Invoices.js` dodatkowo `DOC_TYPE_*`/
// `KSEF_STATUS_*`), które zdążyły się rozjechać — `partial` był raz "Częściowo", raz
// "Częściowo opłacona", a każdy typ dokumentu miał inny, nieznaczący kolor (`VAT #1d4ed8`,
// `PROFORMA #9333ea`...). Moduł jest CZYSTY (bez Reacta/i18n): zwraca `{ tone, labelKey }`,
// gdzie `tone` idzie do `<Badge $tone>` z `ui/common.js` (jedna z `'success'|'danger'|'warning'|'info'`),
// a `labelKey` woła wywołujący przez `t()` — sam moduł nie zna `useTranslation()`.
//
// Kolory typu dokumentu celowo NIE niosą już osobnego znaczenia per typ (VAT/PROFORMA/SIMPLIFIED
// dostają tone='info', CORRECTION dostaje tone='warning' — bo korekta to jedyny typ, który realnie
// zmienia inny dokument). Rozróżnienie między VAT/PROFORMA/SIMPLIFIED niesie WYŁĄCZNIE tekst etykiety.

export const DOC_TYPES = ['VAT', 'PROFORMA', 'SIMPLIFIED', 'CORRECTION'];

// P4.2: z aplikacji do KSeF idą faktury VAT i korekty. Proforma nie jest fakturą, a faktury
// uproszczonej aplikacja nie wysyła (F.21), choć KSeF ją obejmuje.
export const KSEF_DOC_TYPES = ['VAT', 'CORRECTION'];

const DOC_TYPE_LABEL_KEYS = {
  VAT: 'invoices.docType.vat',
  PROFORMA: 'invoices.docType.proforma',
  SIMPLIFIED: 'invoices.docType.simplified',
  CORRECTION: 'invoices.docType.correction',
};

export function docTypeInfo(docType) {
  return {
    tone: docType === 'CORRECTION' ? 'warning' : 'info',
    labelKey: DOC_TYPE_LABEL_KEYS[docType] || null,
  };
}

const PAYMENT_STATUS_TONES = {
  unpaid: 'danger',
  partial: 'warning',
  paid: 'success',
};

const PAYMENT_STATUS_LABEL_KEYS = {
  unpaid: 'invoices.paymentStatus.unpaid',
  partial: 'invoices.paymentStatus.partial',
  paid: 'invoices.paymentStatus.paid',
};

export function paymentStatusInfo(status) {
  return {
    tone: PAYMENT_STATUS_TONES[status] || 'info',
    labelKey: PAYMENT_STATUS_LABEL_KEYS[status] || null,
  };
}

// Należność: TA SAMA reguła co `Login/backend/lib/invoiceSummary.js` (kafelki nad listą) —
// faktura VAT albo uproszczona, wystawiona, nieopłacona w całości. Proforma jest poza nią,
// bo należność powstaje dopiero z faktury (inaczej proforma i faktura po niej liczyłyby się
// podwójnie); skorygowana też, bo korekta ją zeruje. Zmiana tu wymaga zmiany tam.
export const RECEIVABLE_DOC_TYPES = ['VAT', 'SIMPLIFIED'];

export function isReceivable(inv) {
  return !!inv
    && RECEIVABLE_DOC_TYPES.includes(inv.doc_type)
    && inv.status === 'issued'
    && inv.payment_status !== 'paid';
}

// Samą datę („2026-09-21") czytamy jako LOKALNĄ północ: `new Date('2026-09-21')` to północ UTC,
// czyli w strefach na zachód od Greenwich poprzedni dzień — faktura byłaby „po terminie" o dzień
// za wcześnie. Pełny znacznik czasu (tak serializuje się kolumna DATE z node-pg) jest już chwilą.
function parseDueDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Należność, której termin minął przed dzisiejszym dniem. `today` wstrzykiwane w testach. */
export function isOverdue(inv, today = new Date()) {
  if (!isReceivable(inv)) return false;
  const due = parseDueDate(inv.payment_due_date);
  if (!due) return false;
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return due < startOfToday;
}

/** Stan rozliczenia dokumentu: ile zapłacono, ile zostało i jaka to część całości (0–1). */
export function paymentProgress(inv) {
  const total = Number(inv?.total_gross) || 0;
  const paid = Math.max(0, Number(inv?.amount_paid) || 0);
  const remaining = Math.max(0, Math.round((total - paid) * 100) / 100);
  const ratio = total > 0 ? Math.min(1, paid / total) : 0;
  return { total, paid, remaining, ratio };
}

const KSEF_STATUS_TONES = {
  pending: 'info',
  sent: 'warning',
  accepted: 'success',
  rejected: 'danger',
  error: 'danger',
  upo_received: 'success',
};

const KSEF_STATUS_LABEL_KEYS = {
  pending: 'invoices.ksefStatus.pending',
  sent: 'invoices.ksefStatus.sent',
  accepted: 'invoices.ksefStatus.accepted',
  rejected: 'invoices.ksefStatus.rejected',
  error: 'invoices.ksefStatus.error',
  upo_received: 'invoices.ksefStatus.upoReceived',
};

export function ksefStatusInfo(status) {
  return {
    tone: KSEF_STATUS_TONES[status] || 'info',
    labelKey: KSEF_STATUS_LABEL_KEYS[status] || null,
  };
}

// F.16: co zaproponować przy fakturze w kolumnie KSeF. Ponowienie tylko wtedy, gdy wiadomo, że
// faktury w KSeF nie ma. Odrzucenie 440 to duplikat, czyli faktura JUŻ jest w KSeF, a 'sent'
// z niepewną dostawą dokończy odczyt statusu; w obu przypadkach ponowna wysyłka nic nie da albo
// zrobi drugi dokument. Wiersz z innego środowiska niż bieżące (np. przyjęty w testowym KSeF przed
// przejściem na produkcję) nie blokuje wysyłki, więc wraca „Wyślij do KSeF”. Wyjątek: faktura
// zgłoszona do produkcyjnego KSeF, której wysyłka na test zatarłaby numer KSeF i UPO (serwer
// odmawia tak samo, lib/ksefSend.js).
export const KSEF_KOD_DUPLIKATU = '440';
const ZGLOSZONA = ['sent', 'accepted', 'upo_received'];

export function ksefAkcja(ks, currentEnvironment) {
  if (!ks) return { canSend: true, retry: false, noteKey: null };
  if (currentEnvironment && ks.environment && ks.environment !== currentEnvironment) {
    if (ks.environment === 'prod' && ZGLOSZONA.includes(ks.status)) {
      return { canSend: false, retry: false, noteKey: 'invoices.ksef.prodEnvironmentNote' };
    }
    return { canSend: true, retry: false, noteKey: 'invoices.ksef.otherEnvironmentNote' };
  }
  if (ks.status === 'rejected' && String(ks.errorCode) === KSEF_KOD_DUPLIKATU) {
    return { canSend: false, retry: false, noteKey: 'invoices.ksef.duplicateNote' };
  }
  if (ks.status === 'sent' && ks.errorCode === 'delivery_unknown') {
    return { canSend: false, retry: false, noteKey: 'invoices.ksef.deliveryUnknownNote' };
  }
  if (ks.status === 'rejected' || ks.status === 'error') return { canSend: true, retry: true, noteKey: null };
  return { canSend: false, retry: false, noteKey: null };
}
